import { useCallback, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { MEMORY_GAME_TIME_LIMIT_SECONDS } from "../constants/memoryGame";
import {
  countRecallResults,
  evaluateRecallEntries,
  getRecallEncouragement,
  normalizeRecallWord,
} from "../utils/memoryRecall";
import { filterTypedWord, WORD_MOT_MAX_LENGTH } from "../utils/wordSanitize";
import CountDownTimer from "./CountDownTimer";
import "./MemoryRecallGame.css";

function buildInitialEntries(words, prefill) {
  if (prefill) return [...words];
  return Array(words.length).fill("");
}

export default function MemoryRecallGame({
  words,
  prefillEntries = false,
  onComplete,
}) {
  const expectedWords = useMemo(
    () => (Array.isArray(words) ? words.filter(Boolean) : []),
    [words]
  );

  const [entries, setEntries] = useState(() =>
    buildInitialEntries(expectedWords, prefillEntries)
  );
  const [phase, setPhase] = useState("playing");
  const [timerFrozen, setTimerFrozen] = useState(false);
  const [duplicateHint, setDuplicateHint] = useState("");
  const startedAtRef = useRef(Date.now());

  const results = useMemo(() => {
    if (phase !== "results") return [];
    return evaluateRecallEntries(expectedWords, entries);
  }, [phase, expectedWords, entries]);

  const scoreSummary = useMemo(() => countRecallResults(results), [results]);

  const encouragement = useMemo(() => {
    if (phase !== "results") return null;
    return getRecallEncouragement(scoreSummary.correct, expectedWords.length);
  }, [phase, scoreSummary.correct, expectedWords.length]);

  const finishRecall = useCallback(() => {
    setTimerFrozen(true);
    setPhase("results");
    setDuplicateHint("");
  }, []);

  const handleEntryChange = (index, rawValue) => {
    if (phase !== "playing") return;

    setEntries((previous) => {
      const nextValue = filterTypedWord(rawValue);
      const next = [...previous];
      const normalizedValue = normalizeRecallWord(nextValue);

      if (normalizedValue) {
        const duplicateIndex = next.findIndex(
          (entry, entryIndex) =>
            entryIndex !== index && normalizeRecallWord(entry) === normalizedValue
        );

        if (duplicateIndex !== -1) {
          setDuplicateHint("Chaque mot ne peut être saisi qu'une seule fois.");
          return previous;
        }
      }

      setDuplicateHint("");
      next[index] = nextValue;
      return next;
    });
  };

  const handleContinue = useCallback(() => {
    onComplete?.({
      score: scoreSummary.correct,
      totalQuestions: expectedWords.length,
      responseTimeMs: Math.max(0, Date.now() - startedAtRef.current),
    });
  }, [onComplete, scoreSummary.correct, expectedWords.length]);

  const handleClearEntry = (index) => {
    if (phase !== "playing") return;
    setDuplicateHint("");
    setEntries((previous) => {
      const next = [...previous];
      next[index] = "";
      return next;
    });
  };

  if (expectedWords.length === 0) {
    return (
      <div className="memory-recall memory-recall--empty">
        <p>Aucun mot disponible pour le jeu de mémoire.</p>
        <button type="button" className="memory-recall__btn" onClick={onComplete}>
          Continuer
        </button>
      </div>
    );
  }

  return (
    <div className={`memory-recall${phase === "results" ? " memory-recall--results" : ""}`}>
      <header className="memory-recall__header">
        {phase === "playing" ? (
          <div className="memory-recall__intro">
            <h2 className="memory-recall__title">Jeu de mémoire</h2>
            <p className="memory-recall__subtitle">
              Restituez de mémoire les {expectedWords.length} mots de la partie.
              L&apos;ordre n&apos;a pas d&apos;importance.
            </p>
          </div>
        ) : (
          encouragement && (
            <div
              className={`memory-recall__feedback memory-recall__feedback--${encouragement.tier}`}
            >
              <p className="memory-recall__feedback-title">{encouragement.title}</p>
              <p className="memory-recall__feedback-detail">{encouragement.detail}</p>
            </div>
          )
        )}
      </header>

      <div className="memory-recall__controls">
        {phase === "playing" ? (
          <div className="memory-recall__controls-playing">
            <div className="memory-recall__timer-wrap">
              <CountDownTimer
                timeLimit={MEMORY_GAME_TIME_LIMIT_SECONDS}
                onTimesUp={finishRecall}
                isFrozen={timerFrozen}
              />
            </div>
            <button
              type="button"
              className="memory-recall__btn memory-recall__btn--finish"
              onClick={finishRecall}
            >
              Terminer
            </button>
          </div>
        ) : (
          <div className="memory-recall__expected-panel">
            <p className="memory-recall__expected">
              Les mots de la partie étaient&nbsp;:{" "}
              <span>{expectedWords.join(", ")}</span>
            </p>
            <button
              type="button"
              className="memory-recall__btn memory-recall__btn--continue"
              onClick={handleContinue}
            >
              Continuer
            </button>
          </div>
        )}
      </div>

      <p
        className={`memory-recall__hint-slot${
          duplicateHint && phase === "playing" ? " memory-recall__hint-slot--visible" : ""
        }`}
        role="status"
        aria-live="polite"
      >
        {duplicateHint || "\u00a0"}
      </p>

      <div className="memory-recall__grid" role="list">
        {expectedWords.map((_, index) => {
          const status = results[index];
          return (
            <div
              key={`memory-slot-${index}`}
              className={`memory-recall__cell${
                status ? ` memory-recall__cell--${status}` : ""
              }`}
              role="listitem"
            >
              <span className="memory-recall__cell-index">{index + 1}</span>
              <input
                type="text"
                className="memory-recall__input"
                value={entries[index] ?? ""}
                onChange={(event) => handleEntryChange(index, event.target.value)}
                maxLength={WORD_MOT_MAX_LENGTH}
                disabled={phase !== "playing"}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-label={`Mot ${index + 1}`}
              />
              <button
                type="button"
                className="memory-recall__clear"
                onClick={() => handleClearEntry(index)}
                disabled={phase !== "playing" || !(entries[index] ?? "").trim()}
                aria-label={`Vider la case ${index + 1}`}
              >
                <X size={13} strokeWidth={2.5} aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
